import { useContext, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { fetchData } from '@/lib/api/fetch';
import { AuthContext } from '@/auth/Authentication';
import { UploadExistsResponse, UploadPreSignResponse, UploadUrlResponse } from '@/lib/types';
import { AppToaster } from '@/src/pages/_app';

type UploadedFile = { name: string; url: string };

type UploadProps = {
  file: File;
  /* Update the file if exists. */
  update?: boolean;
};

export type UploadFolder = 'konfiguraator' | 'paywall';

type Props = {
  onCompleted: (completed: UploadedFile) => void;
  onExisting: (fileName: string) => void;
  folder?: UploadFolder;
};

const accept = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/svg+xml': ['.svg'],
  'image/tiff': ['.tif', '.tiff'],
};

const useUpload = ({ onCompleted, onExisting, folder = 'konfiguraator' }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [existingFile, setExistingFile] = useState<File | null>(null);
  const { jwt } = useContext(AuthContext);

  const updateExisting = () => {
    if (!existingFile) {
      return;
    }
    void upload({
      file: existingFile,
      update: true,
    });
    setExistingFile(null);
  };

  const clearExisting = () => {
    setExistingFile(null);
    setLoading(false);
  };

  const upload = async (payload: UploadProps) => {
    setLoading(true);

    /* Check if the file has an existing version. */
    if (!payload.update) {
      const isExisting = await exists(payload.file);
      if (isExisting) {
        setExistingFile(payload.file);
        onExisting(payload.file.name);
        return;
      }
    }

    const url = await sendToStorage(payload.file);
    if (!url) {
      AppToaster.addToast({
        intent: 'danger',
        text: t('common.error'),
      });
    } else {
      AppToaster.addToast({
        intent: 'success',
        text: t('upload.success'),
      });
      onCompleted({
        name: payload.file.name,
        url,
      });
    }

    setLoading(false);
  };

  const sendToStorage = async (file: File) => {
    /* Pre-sign. */
    const preSignedUrl = await preSign(file);
    if (!preSignedUrl) {
      return false;
    }
    /* Put to pre-signed url. */
    const success = await put(preSignedUrl, file);
    if (!success) {
      return false;
    }
    return getUrl(file);
  };

  const put = async (url: string, file: File) => {
    try {
      const result = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });
      return result.ok;
    } catch {
      return false;
    }
  };

  const preSign = async (file: File) => {
    const result = await fetchData<UploadPreSignResponse>('/upload/pre-sign', {
      method: 'POST',
      body: getFileMeta(file),
      jwt,
    });
    if (result.hasError) {
      return null;
    }
    return result.data!.url;
  };

  const exists = async (file: File) => {
    const result = await fetchData<UploadExistsResponse>('/upload/exists', {
      params: getFileMeta(file),
      jwt,
    });
    if (result.hasError) {
      return false;
    }
    return result.data!.exists;
  };

  const getUrl = async (file: File) => {
    const result = await fetchData<UploadUrlResponse>('/upload/url', {
      params: getFileMeta(file),
      jwt,
    });
    if (result.hasError) {
      return null;
    }
    return result.data!.url;
  };

  const getFileMeta = (file: File) => {
    return {
      name: folder ? `${folder}/${file.name}` : file.name,
      type: file.type,
    };
  };

  return {
    accept,
    upload: (payload: UploadProps) => void upload(payload),
    existing: {
      file: existingFile,
      update: updateExisting,
      clear: clearExisting,
    },
    loading,
  };
};

export default useUpload;
